import React, { useEffect, useState, useMemo } from 'react';
import { calibrationReportAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  BarChart3, CheckCircle, AlertTriangle, Target, Clock,
  TrendingUp, Filter, X, Download
} from 'lucide-react';

function MiniBar({ planned, achieved, maxVal, pct, monthLabel, onClick }) {
  const pw = maxVal > 0 ? (planned / maxVal) * 100 : 0;
  const aw = maxVal > 0 ? (achieved / maxVal) * 100 : 0;
  return (
    <div className="flex-1 min-w-[48px] text-center">
      <div className="flex items-end gap-1 h-24">
        <div className="flex-1 flex flex-col items-center gap-0.5">
          <span className="text-[9px] font-mono text-slate-400">{planned}</span>
          <div className="w-full bg-slate-200 rounded-t" style={{ height: `${Math.max(pw, 4)}%` }} />
        </div>
        <div className="flex-1 flex flex-col items-center gap-0.5">
          <span className="text-[9px] font-mono text-emerald-600">{achieved}</span>
          <div className="w-full bg-emerald-500 rounded-t" style={{ height: `${Math.max(aw, 4)}%` }} />
        </div>
      </div>
      <p className="text-[9px] text-slate-400 mt-1 font-mono">{monthLabel}</p>
      <button
        onClick={onClick}
        className={`text-[10px] font-bold mt-0.5 cursor-pointer hover:underline ${
          pct >= 90 ? 'text-emerald-600' : pct >= 60 ? 'text-amber-600' : pct > 0 ? 'text-red-600' : 'text-slate-300'
        }`}
        data-testid={`month-pct-${monthLabel}`}
      >
        {pct}%
      </button>
    </div>
  );
}

export default function CalibrationReportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);

  const today = new Date();
  const defaultFrom = new Date(today.getFullYear(), today.getMonth() - 11, 1).toISOString().slice(0, 10);
  const defaultTo = today.toISOString().slice(0, 10);

  const [fromDate, setFromDate] = useState(defaultFrom);
  const [toDate, setToDate] = useState(defaultTo);

  const [includeInactive, setIncludeInactive] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadMasterList = async () => {
    setDownloading(true);
    try {
      const res = await calibrationReportAPI.exportMasterList({ include_inactive: includeInactive });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Master_List_of_Instruments_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Master list downloaded');
    } catch (err) {
      toast.error('Failed to download master list');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => { loadReport(); }, []);

  const loadReport = async (from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (from || fromDate) params.from_date = from || fromDate;
      if (to || toDate) params.to_date = to || toDate;
      const res = await calibrationReportAPI.get(params);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load calibration report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    if (fromDate > toDate) { toast.error('From date must be before To date'); return; }
    loadReport(fromDate, toDate);
  };

  const maxMonthlyVal = useMemo(() => {
    if (!data?.monthly) return 10;
    return Math.max(...data.monthly.map(m => Math.max(m.planned, m.achieved)), 1);
  }, [data]);

  const selectedMonthData = useMemo(() => {
    if (!selectedMonth || !data?.monthly) return null;
    return data.monthly.find(m => m.month === selectedMonth);
  }, [selectedMonth, data]);

  if (loading && !data) {
    return <div className="flex items-center justify-center h-64"><p className="text-sm text-slate-400">Loading report...</p></div>;
  }

  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="calibration-report-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">Calibration Report</h1>
          <p className="text-sm text-slate-500 mt-0.5">Monthly planned vs achieved calibrations</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" data-testid="include-inactive-label">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 accent-[#0F172A]"
              data-testid="include-inactive-checkbox"
            />
            Include non-active / retired
          </label>
          <Button
            onClick={handleDownloadMasterList}
            disabled={downloading}
            size="sm"
            variant="outline"
            className="gap-1.5 border-slate-300"
            data-testid="download-master-list-btn"
          >
            <Download className="w-3.5 h-3.5" />
            {downloading ? 'Preparing...' : 'Download Master List (Excel)'}
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <Card className="border border-slate-200 shadow-none" data-testid="date-filter-card">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs font-medium text-slate-500 mb-1 block">From</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40 text-sm" data-testid="filter-from-date" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500 mb-1 block">To</Label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40 text-sm" data-testid="filter-to-date" />
            </div>
            <Button onClick={handleFilter} disabled={loading} size="sm" className="bg-[#0F172A] hover:bg-[#1E293B] text-white gap-1.5" data-testid="apply-filter-btn">
              <Filter className="w-3.5 h-3.5" /> {loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-summary-cards">
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center mb-2"><Target className="w-3.5 h-3.5 text-slate-600" /></div>
            <p className="text-2xl font-bold text-[#0F172A]" data-testid="total-planned">{s.total_planned || 0}</p>
            <p className="text-xs text-slate-500">Total Planned</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="w-7 h-7 rounded-md bg-emerald-50 flex items-center justify-center mb-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-600" /></div>
            <p className="text-2xl font-bold text-emerald-700" data-testid="total-achieved">{s.total_achieved || 0}</p>
            <p className="text-xs text-slate-500">Total Achieved</p>
            <p className="text-[10px] text-emerald-600 font-medium mt-0.5">{s.achievement_rate || 0}% rate</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center mb-2"><TrendingUp className="w-3.5 h-3.5 text-blue-600" /></div>
            <p className="text-2xl font-bold text-blue-700" data-testid="total-on-time">{s.total_on_time || 0}</p>
            <p className="text-xs text-slate-500">On-Time</p>
            <p className="text-[10px] text-blue-600 font-medium mt-0.5">{s.on_time_rate || 0}% of achieved</p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <div className="w-7 h-7 rounded-md bg-red-50 flex items-center justify-center mb-2"><AlertTriangle className="w-3.5 h-3.5 text-red-600" /></div>
            <p className="text-2xl font-bold text-red-700" data-testid="total-past-due">{s.total_past_due || 0}</p>
            <p className="text-xs text-slate-500">Past Due</p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Chart with clickable % */}
      <Card className="border border-slate-200 shadow-none" data-testid="monthly-chart-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <BarChart3 className="w-4 h-4" /> Monthly Planned vs Achieved
            </CardTitle>
            <div className="flex items-center gap-3 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-300 inline-block" /> Planned</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500 inline-block" /> Achieved</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {data?.monthly?.length > 0 ? (
            <div className="flex items-end gap-1 overflow-x-auto pb-2">
              {data.monthly.map((m) => (
                <MiniBar
                  key={m.month}
                  planned={m.planned}
                  achieved={m.achieved}
                  maxVal={maxMonthlyVal}
                  pct={m.achievement_pct || 0}
                  monthLabel={m.month_label}
                  onClick={() => setSelectedMonth(m.month)}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-8 text-center">No data for selected period</p>
          )}
        </CardContent>
      </Card>

      {/* Monthly Breakdown Table */}
      <Card className="border border-slate-200 shadow-none" data-testid="monthly-breakdown-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-[#0F172A]">Monthly Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold text-slate-500">Month</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">Planned</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">Achieved</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">On-Time</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">Past Due</TableHead>
                <TableHead className="text-xs font-semibold text-slate-500 text-right">Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.monthly?.map((m) => (
                <TableRow key={m.month} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedMonth(m.month)} data-testid={`month-row-${m.month}`}>
                  <TableCell className="text-sm font-medium">{m.month_label}</TableCell>
                  <TableCell className="text-sm text-right font-mono">{m.planned}</TableCell>
                  <TableCell className="text-sm text-right font-mono text-emerald-600 font-semibold">{m.achieved}</TableCell>
                  <TableCell className="text-sm text-right font-mono text-blue-600">{m.on_time}</TableCell>
                  <TableCell className="text-sm text-right font-mono">
                    {m.past_due > 0 ? <span className="text-red-600 font-semibold">{m.past_due}</span> : <span className="text-slate-300">0</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className={`text-[10px] cursor-pointer ${
                      m.achievement_pct >= 90 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      m.achievement_pct >= 60 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      m.achievement_pct > 0 ? 'bg-red-50 text-red-700 border-red-200' :
                      'bg-slate-50 text-slate-400 border-slate-200'
                    }`}>
                      {m.achievement_pct}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Month Detail Dialog */}
      <Dialog open={!!selectedMonth} onOpenChange={() => setSelectedMonth(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="month-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{selectedMonthData?.month_label} — Planned vs Achieved ({selectedMonthData?.achievement_pct}%)</span>
            </DialogTitle>
          </DialogHeader>
          {selectedMonthData && (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <span className="px-2 py-1 bg-slate-100 rounded text-slate-700">Planned: <strong>{selectedMonthData.planned}</strong></span>
                <span className="px-2 py-1 bg-emerald-50 rounded text-emerald-700">Achieved: <strong>{selectedMonthData.achieved}</strong></span>
                <span className="px-2 py-1 bg-blue-50 rounded text-blue-700">On-Time: <strong>{selectedMonthData.on_time}</strong></span>
                <span className="px-2 py-1 bg-red-50 rounded text-red-700">Past Due: <strong>{selectedMonthData.past_due}</strong></span>
              </div>

              <h4 className="text-sm font-semibold text-slate-700 mt-4">Planned Instruments</h4>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="text-xs">Instrument</TableHead>
                    <TableHead className="text-xs">Code</TableHead>
                    <TableHead className="text-xs">Due Date</TableHead>
                    <TableHead className="text-xs">Actual Date</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(selectedMonthData.planned_items || []).map((item, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs font-medium">{item.tool_name}</TableCell>
                      <TableCell className="text-xs text-slate-500">{item.instrument_code || item.serial_number}</TableCell>
                      <TableCell className="text-xs font-mono">{item.due_date}</TableCell>
                      <TableCell className="text-xs font-mono">{item.actual_date || <span className="text-amber-500">Pending</span>}</TableCell>
                      <TableCell>
                        {item.status === 'completed' ? (
                          item.actual_date && item.actual_date <= item.due_date
                            ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700">On-Time</Badge>
                            : <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700">Late</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700">Pending</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!selectedMonthData.planned_items || selectedMonthData.planned_items.length === 0) && (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-400 text-xs py-4">No planned items</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>

              {selectedMonthData.achieved_items?.length > 0 && (
                <>
                  <h4 className="text-sm font-semibold text-slate-700 mt-4">Completed Calibrations</h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead className="text-xs">Instrument</TableHead>
                        <TableHead className="text-xs">Due Date</TableHead>
                        <TableHead className="text-xs">Actual Date</TableHead>
                        <TableHead className="text-xs">Performed By</TableHead>
                        <TableHead className="text-xs">Result</TableHead>
                        <TableHead className="text-xs">Timeliness</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedMonthData.achieved_items.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs font-medium">{item.tool_name}</TableCell>
                          <TableCell className="text-xs font-mono">{item.due_date}</TableCell>
                          <TableCell className="text-xs font-mono">{item.actual_date}</TableCell>
                          <TableCell className="text-xs text-slate-500">{item.performed_by}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-[10px] ${item.result === 'pass' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                              {item.result}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {item.on_time
                              ? <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700">On-Time</Badge>
                              : <Badge variant="outline" className="text-[10px] bg-red-50 text-red-700">Late</Badge>
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
